import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation,
  type OnInit
} from '@angular/core';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { NotificationKindEnum } from '@zaoblako/shared/modules/notification/enums/notification.kind.enum';
import { ListSearchService } from '@zaoblako/web/core/services/list-search.service';
import { AuthService } from '@zaoblako/web/core/services/security/auth.service';
import { LkEuNavbarComponent } from '@zaoblako/web/modules/lk/external-user/dumbs/navbar/lk-eu-navbar.component';
import { LkEuNotificationIconComponent } from '@zaoblako/web/modules/lk/external-user/dumbs/notification-icon/lk-eu-notification-icon.component';
import { LkEuNotificationSidebarComponent } from '@zaoblako/web/modules/lk/external-user/dumbs/notification-sidebar/lk-eu-notification-sidebar.component';
import { LkEuNotificationPanelComponent } from '@zaoblako/web/modules/lk/external-user/dumbs/notification-panel/lk-eu-notification-panel.component';
import { FormSidebarComponent } from '@zaoblako/web/shared/components/form-sidebar/form-sidebar.component';
import { UserAvatarComponent } from '@zaoblako/web/shared/components/user-avatar/user-avatar.component';
import { TruncatePipe } from '@zaoblako/web/shared/pipes/truncate.pipe';
import { PersonalAccountMenuService } from '@zaoblako/web/core/services/lk/personal-account-menu.service';
import { MenuItem } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { DialogService } from 'primeng/dynamicdialog';
import { FrmAnimatedLogoComponent } from '@zaoblako/web/shared/components/frm-animated-logo/frm-animated-logo.component';
import { SupportModalComponent } from '@zaoblako/web/shared/components/support-modal/support-modal.component';
import { isEmpty } from 'lodash';
import { SkeletonModule } from 'primeng/skeleton';
import type { PersonalAccountMenuItemFindInterface } from '@zaoblako/shared/modules/security/dtos/personal-account/menu-item/personal-account-menu-item-find.dto';

@Component({
  selector: 'crm-external-user-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    UserAvatarComponent,
    LkEuNavbarComponent,
    BadgeModule,
    LkEuNotificationIconComponent,
    LkEuNotificationSidebarComponent,
    FormSidebarComponent,
    LkEuNotificationPanelComponent,
    TruncatePipe,
    SupportModalComponent,
    FrmAnimatedLogoComponent,
    SkeletonModule
  ],
  providers: [DialogService, ListSearchService],
  templateUrl: './external-user-layout.component.html',
  styleUrls: ['./external-user-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class ExternalUserLayoutComponent implements OnInit {
  @Input() public isShow = false;
  @Input() public selectedInformerType: NotificationKindEnum.HELPDESK | NotificationKindEnum.PERSONAL | NotificationKindEnum.PASSAGE | 'FAVORITE' =
    NotificationKindEnum.PERSONAL;

  @Output() public isShowChange = new EventEmitter<boolean>();

  public menuLoading = false;

  public menuItems: MenuItem[] = [];

  @ViewChild(LkEuNotificationSidebarComponent)
  private readonly notificationSidebar?: LkEuNotificationSidebarComponent;

  @ViewChild('navbar')
  public readonly navbar?: LkEuNavbarComponent;

  public constructor(
    public readonly authService: AuthService,
    private readonly personalAccountMenuService: PersonalAccountMenuService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    this.loadMenu();
  }

  public onMobileNavBurgerClick = (event: Event): void => {
    event.stopPropagation();
    this.navbar?.toggleMobileMenu();
  };

  public onNavbarMobileMenuOpenChange = (): void => {
    this.changeDetectorRef.markForCheck();
  };

  public openNotificationSidebar(
    type?: NotificationKindEnum.HELPDESK | NotificationKindEnum.PERSONAL | NotificationKindEnum.PASSAGE | 'FAVORITE'
  ): void {
    if (type) {
      this.selectedInformerType = type;
    }

    const alreadyOpen = this.notificationSidebar?.sidebarVisible === true;

    if (alreadyOpen) {
      if (type) {
        this.changeDetectorRef.markForCheck();
      }
      return;
    }

    this.isShow = true;
    this.notificationSidebar?.open();
  }

  public onNotificationSidebarVisibleChange(visible: boolean): void {
    if (visible || !this.isShow) {
      return;
    }

    this.isShow = false;
    this.changeDetectorRef.markForCheck();
  }

  private loadMenu() {
    this.menuLoading = true;
    this.changeDetectorRef.markForCheck();

    this.personalAccountMenuService
      .getMenu()
      .subscribe({
        next: ({ items }) => {
          this.menuItems = this.transformToPrimeNgMenu(items);
          this.menuLoading = false;
          this.changeDetectorRef.markForCheck();
        },
        error: () => {
          this.menuItems = [];
          this.menuLoading = false;
          this.changeDetectorRef.markForCheck();
        }
      })
  }

  private transformToPrimeNgMenu(items: Record<string, PersonalAccountMenuItemFindInterface>): MenuItem[] {
    const menuItems: MenuItem[] = [];

    const iteratee = (item: PersonalAccountMenuItemFindInterface, parent?: MenuItem): void => {
      if (!item.enabled) {
        return;
      }
      const frontendItem: MenuItem = { label: item.displayName };

      if (!isEmpty(item.children)) {
        frontendItem.items = [];
        for (const child of Object.values(item.children)) {
          iteratee(child, frontendItem);
        }
      } else if (item.route) {
        frontendItem.routerLink = item.route;
      }

      if (parent) {
        parent.items.push(frontendItem);
      } else {
        menuItems.push(frontendItem);
      }
    };
    for (const item of Object.values(items)) {
      iteratee(item);
    }

    return menuItems;
  }
}
